import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addActualPaxEntry,
  createReservation,
  getActualPaxEntries,
  getReservation,
  getReservations,
  updateReservation
} from '../api/reservationsApi';
import { queryKeys } from '../api/queryKeys';
import { dateInputValue } from '../utils/date';

export function useReservations(params = {}, options = {}) {
  return useQuery({
    queryKey: queryKeys.reservations(params),
    queryFn: () => getReservations(params),
    ...options
  });
}

export function useTodayReservations(date = dateInputValue(), params = {}, options = {}) {
  const scopedParams = { start: date, end: date, ...params };
  return useQuery({
    queryKey: queryKeys.todayReservations(scopedParams),
    queryFn: () => getReservations(scopedParams),
    ...options
  });
}

export function useUpcomingReservations(params = {}, options = {}) {
  return useQuery({
    queryKey: queryKeys.upcomingReservations(params),
    queryFn: () => getReservations(params),
    ...options
  });
}

export function useReservation(recordId, options = {}) {
  return useQuery({
    queryKey: queryKeys.reservation(recordId),
    queryFn: () => getReservation(recordId),
    enabled: Boolean(recordId) && options.enabled !== false,
    ...options
  });
}

export function useActualPaxEntries(recordId, options = {}) {
  return useQuery({
    queryKey: queryKeys.actualPaxEntries(recordId),
    queryFn: () => getActualPaxEntries(recordId),
    enabled: Boolean(recordId) && options.enabled !== false,
    ...options
  });
}

export function useCreateReservation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createReservation,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['reservations'] })
  });
}

export function useUpdateReservation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ recordId, fields }) => updateReservation(recordId, fields),
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      if (data?.record?.id) queryClient.invalidateQueries({ queryKey: queryKeys.reservation(data.record.id) });
    }
  });
}

export function useAddActualPaxEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ recordId, payload }) => addActualPaxEntry(recordId, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.actualPaxEntries(variables.recordId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.reservation(variables.recordId) });
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
    }
  });
}
